import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export type IssueType = 'wrong_name' | 'wrong_price' | 'wrong_image' | 'wrong_store' | 'missing_link' | 'other' | 'needs_review';

export const ISSUE_LABELS: Record<IssueType, string> = {
  wrong_name: 'Wrong name',
  wrong_price: 'Wrong price',
  wrong_image: 'Wrong image',
  wrong_store: 'Wrong store/brand',
  missing_link: 'Missing link',
  needs_review: 'Needs review',
  other: 'Other',
};

interface SubmitIssueParams {
  savedItemId: string;
  issues: IssueType[];
  description?: string;
}

export function useSubmitItemIssue() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ savedItemId, issues, description }: SubmitIssueParams) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('item_data_issues' as any)
        .insert({
          saved_item_id: savedItemId,
          user_id: user.id,
          issues,
          description: description || null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Thanks! We\'ll look into it.');
      queryClient.invalidateQueries({ queryKey: ['item-data-issues'] });
    },
    onError: () => {
      toast.error('Failed to submit report');
    },
  });
}

export function useReportedItemIds() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['item-data-issues', 'my-reported', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_data_issues')
        .select('saved_item_id')
        .eq('user_id', user!.id)
        .in('status', ['pending', 'reviewed']);
      if (error) throw error;
      return new Set((data ?? []).map((d: any) => d.saved_item_id as string));
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminItemIssues() {
  return useQuery({
    queryKey: ['item-data-issues', 'admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('item_data_issues' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as any[];
    },
  });
}

export function useUpdateItemIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: string; status: string; adminNotes?: string }) => {
      const updates: any = { status };
      if (adminNotes !== undefined) updates.admin_notes = adminNotes;
      const { error } = await supabase
        .from('item_data_issues' as any)
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-data-issues'] });
      toast.success('Issue updated');
    },
    onError: () => {
      toast.error('Failed to update issue');
    },
  });
}
